import { DAYS, BASE_SITE } from '~~/constants';
import type { Horario } from '~~/interfaces';
import { formatToISO8601, durationToISO8601 } from './hora';

interface StationGeo {
  name: string;
  lat: number;
  lng: number;
}

interface TripSchemaParams {
  serviceName: string;
  estacionSalida: StationGeo;
  estacionLlegada: StationGeo;
  tarifa?: number;
  duracion?: string;
}

interface ScheduleListItem {
  '@type': string;
  position: number;
  item: Record<string, unknown>;
}

export const buildTripSchema = (
  params: TripSchemaParams
): Record<string, unknown> => {
  const json: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Trip',
    name: `${params.serviceName} ${params.estacionSalida.name} a ${params.estacionLlegada.name}`,
    description: `Viaje en tren desde ${params.estacionSalida.name} hasta ${params.estacionLlegada.name}`,
    departureLocation: {
      '@type': 'Place',
      name: `Estación ${params.estacionSalida.name}`,
      geo: {
        '@type': 'GeoCoordinates',
        latitude: params.estacionSalida.lat,
        longitude: params.estacionSalida.lng,
      },
    },
    arrivalLocation: {
      '@type': 'Place',
      name: `Estación ${params.estacionLlegada.name}`,
      geo: {
        '@type': 'GeoCoordinates',
        latitude: params.estacionLlegada.lat,
        longitude: params.estacionLlegada.lng,
      },
    },
    offers: {
      '@type': 'Offer',
      priceCurrency: 'CLP',
      price: params.tarifa ?? '',
      pricingDescription: 'Tarifa general',
    },
  };

  if (params.duracion) {
    json.duration = params.duracion;
  }

  return json;
};

interface ScheduleListSchemaParams {
  estacionSalidaName: string;
  estacionLlegadaName: string;
  horarios: Horario[];
}

export const buildScheduleListSchema = (
  params: ScheduleListSchemaParams
): Record<string, unknown> => {
  if (!params.horarios.length) return {};

  const items: ScheduleListItem[] = params.horarios.map((schedule, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    item: {
      '@type': 'Trip',
      name: `Tren ${schedule.S}`,
      departureTime: formatToISO8601(schedule.S),
      arrivalTime: formatToISO8601(schedule.T),
      duration: durationToISO8601(
        parseInt(schedule.duracion.replace('min', ''), 10)
      ),
      provider: {
        '@type': 'Organization',
        name: 'EFE',
      },
      dayOfWeek: schedule.V.map((v) => DAYS[v]).flat(),
    },
  }));

  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: `Horarios disponibles ${params.estacionSalidaName} a ${params.estacionLlegadaName}`,
    itemListElement: items,
  };
};

interface BreadcrumbItem {
  name: string;
  url: string;
}

interface BreadcrumbSchemaParams {
  items: BreadcrumbItem[];
}

export const buildBreadcrumbSchema = (
  params: BreadcrumbSchemaParams
): Record<string, unknown> => {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: params.items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
};

interface TransitMapSchemaParams {
  serviceName: string | undefined;
  servicioSlug: string;
  estaciones: string[];
  siteUrl: string;
}

export const buildTransitMapSchema = (
  params: TransitMapSchemaParams
): Record<string, unknown> => {
  return {
    '@context': 'https://schema.org',
    '@type': ['LocalBusiness', 'TransitMap'],
    name: BASE_SITE.TITLE,
    url: `${params.siteUrl}/${params.servicioSlug}`,
    service: [
      {
        '@type': 'Service',
        name: 'Consulta de horarios',
        description: 'Búsqueda de horarios de tren entre estaciones',
        provider: {
          '@type': 'Organization',
          name: params.serviceName,
        },
        availableChannel: {
          '@type': 'ServiceChannel',
          serviceUrl: `${params.siteUrl}/${params.servicioSlug}`,
        },
      },
      {
        '@type': 'Service',
        name: 'Mapa de estaciones',
        description: 'Ubicación de todas las estaciones ' + params.serviceName,
        provider: {
          '@type': 'Organization',
          name: params.serviceName,
        },
        availableChannel: {
          '@type': 'ServiceChannel',
          serviceUrl: `${params.siteUrl}/${params.servicioSlug}/mapa`,
        },
      },
    ],
    areaServed: params.estaciones,
    transportationMode: 'Train',
  };
};
